import { Mark } from '@tiptap/core';
import { Plugin, PluginKey, Transaction } from '@tiptap/pm/state';
import { getMarkRange } from '../../utils/getMarkRange';
import { isUndoRedo } from '../../utils/transactionUtils';
import { AddMarkStep, RemoveMarkStep } from '@tiptap/pm/transform';
import { isEmpty } from 'lodash';
import { MarkType } from '@tiptap/pm/model';
import { getNewTransactionId } from '../../../../client/utils/undoRedo/undoRedoTransactionManager';

import { findAnchorElement } from '../../../../client/utils/dom/domEventTargetUtils';
import { INLINE_COMMENT_CLASS, isEventOnInlineComment } from './inlineCommentDomUtils';

declare module '@tiptap/core' {
    // oxlint-disable-next-line typescript/consistent-type-definitions -- augmenting an interface needs declaration merging
    interface Commands<ReturnType> {
        inlineComment: {
            /**
             * Removes the inline comment mark at the current selection.
             */
            removeInlineCommentAtPos: (pos: number, transactionId: number) => ReturnType;
        };
    }
}

/**
 * These properties are optionally set via CSS variables on a container for the element.
 * See useAttachedCommentStyles for how these are set.
 * Light and dark colours are the light- and dark-theme values behind --color-bg-user-primary.
 * They're primitives because a coloured card's background, not the app theme, picks between them.
 */
const getStyleColorCssProperties = (id: string): string =>
    `--border-width: var(--inline-comment-${id}-border-width, 1px); ` +
    `--light-color: var(--inline-comment-${id}-light-color, var(--color-royal-blue-25)); ` +
    `--dark-color: var(--inline-comment-${id}-dark-color, var(--color-gray-light-mode-900)); ` +
    `--light-border-color: var(--inline-comment-${id}-dark-color, var(--color-border-user)); ` +
    `--dark-border-color: var(--inline-comment-${id}-light-color, var(--color-border-user)); ` +
    `--select-color: var(--inline-comment-${id}-select-color); ` +
    `color: var(--inline-comment-${id}-text-color, var(--ws-element-primary));`;

export type InlineCommentOptions = {
    dispatchToggleInlineCommentPopup: (commentThreadId: string) => void;
    dispatchRestoreCommentThreads: (commentThreadIds: string[], transactionId: number) => void;
    dispatchMoveCommentThreadsToTrash: (commentThreadIds: string[], transactionId: number) => void;
    getNewTransactionId: () => number;
    clickableLinksWhenEditable: boolean;
    onInsertedFromInput?: () => void;
};

const isAddMarkTransaction = (transaction: Transaction) =>
    transaction.steps.some((step) => step instanceof AddMarkStep);

const getMarkCommentThreadIds = (transaction: Transaction, type: MarkType): string[] =>
    transaction.steps
        .filter((step) => (step instanceof AddMarkStep || step instanceof RemoveMarkStep) && step.mark.type === type)
        .map((step) => (step as AddMarkStep).mark.attrs.commentThreadId);

export const InlineComment = Mark.create<InlineCommentOptions>({
    name: 'inlineComment',

    // Ensure the priority is higher than Link extension, so that the InlineComment mark wraps the Link mark
    priority: 1001,

    renderHTML({ HTMLAttributes }) {
        return [
            'span',
            {
                class: INLINE_COMMENT_CLASS,
                'data-comment-thread-id': HTMLAttributes.commentThreadId,
                style: getStyleColorCssProperties(HTMLAttributes.commentThreadId),
            },
            0,
        ];
    },

    addCommands() {
        return {
            removeInlineCommentAtPos:
                (pos: number, transactionId: number) =>
                ({ state, chain }) => {
                    const $pos = state.doc.resolve(pos);
                    const markRange = getMarkRange($pos, this.type);

                    if (!markRange) {
                        console.warn(
                            'InlineComment.removeInlineCommentAtPos - No inline comment mark at the given position',
                        );
                        return false;
                    }

                    return chain()
                        .setTextSelection(markRange)
                        .unsetMark(this.type.name)
                        .flushPendingUpdate({ transactionId })
                        .run();
                },
        };
    },

    addAttributes() {
        return {
            commentThreadId: null,
        };
    },

    /**
     * Detect a ProseMirror undo transaction that's restoring an inline comment.
     * When this occurs, we need to restore the comment thread element that backs it,
     * otherwise the popup will be empty.
     *
     * Milanote undo will use a transaction ID to restore both the inline comment & the comment thread,
     * however a Tiptap undo won't trigger the Milanote undo transaction.
     */
    onTransaction({ transaction, editor }) {
        if (!transaction.docChanged) return;
        if (!isUndoRedo([transaction])) return;

        const commentThreadIds = getMarkCommentThreadIds(transaction, this.type);

        if (isEmpty(commentThreadIds)) return;

        const transactionId = getNewTransactionId();

        isAddMarkTransaction(transaction)
            ? this.options.dispatchRestoreCommentThreads(commentThreadIds, transactionId)
            : this.options.dispatchMoveCommentThreadsToTrash(commentThreadIds, transactionId);

        editor.commands.flushPendingUpdate({ transactionId });
    },

    addProseMirrorPlugins() {
        const { clickableLinksWhenEditable } = this.options;
        return [
            new Plugin({
                key: new PluginKey('inlineCommentClickHandler'),
                props: {
                    handleDOMEvents: {
                        click(view, event) {
                            // view.editable is true when editing - we don't want to open the popup in that case
                            if (view.editable && !clickableLinksWhenEditable) return;

                            if (!isEventOnInlineComment(event.target)) return;

                            event.stopPropagation();
                            event.preventDefault();
                        },
                    },
                    handleClick: (view, pos, event) => {
                        const { state } = view;

                        // view.editable is true when editing - we don't want to open the popup in that case
                        if (view.editable && !clickableLinksWhenEditable) return false;

                        const $pos = state.doc.resolve(pos);
                        const node = $pos.nodeAfter;

                        // Don't handle clicks on links - the inline comment popup can be opened using
                        //  the comment toolbar button instead.
                        if (findAnchorElement(event.target)) return false;

                        const commentThreadId = node?.marks.find((mark) => mark.type.name === this.name)?.attrs
                            .commentThreadId;

                        if (!commentThreadId) return false;

                        this.options.dispatchToggleInlineCommentPopup(commentThreadId);

                        // Prevent default click handling
                        return true;
                    },
                },
            }),
        ];
    },
});
